import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { IconArchive, IconArrowBackUp, IconFileImport, IconPencil } from "@tabler/icons-react";
import {
  AdminPageHeader,
  Badge,
  BotonExportarExcel,
  Button,
  Card,
  Group,
  IconAction,
  Input,
  PaginadorTabla,
  Stack,
  TablaSkeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import { qk } from "@/lib/query-keys";
import { formatearMonto } from "@/shared/format/money";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { OrdenCompraAdmin, OrdenCompraResumen } from "@/lib/types";
import { useDarDeBajaOrdenCompra, useOrdenesCompraAdmin, useReactivarOrdenCompra } from "../hooks/use-ordenes-compra";
import {
  importarOrdenXml,
  listarOrdenesCompra,
  previewOrdenXml,
} from "../services/ordenes-compra-service";
import { OrdenPreviewModal } from "./OrdenPreviewModal";

const PAGE_SIZE = 15;

export function AdminOrdenesCompraPage() {
  const navigate = useNavigate();
  const confirm = useConfirmDialog();
  const [filtro, setFiltro] = useState("");
  const [page, setPage] = useState(1);
  const [filtroDebounced] = useDebouncedValue(filtro, 350);

  // Búsqueda + paginado SERVER-SIDE (query params con debounce).
  const serverParams = useMemo(
    () => ({
      ...(filtroDebounced.trim() ? { busqueda: filtroDebounced.trim() } : {}),
      page,
      pageSize: PAGE_SIZE,
    }),
    [filtroDebounced, page],
  );

  // Cambiar la búsqueda invalida la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtroDebounced]);

  const { data } = useOrdenesCompraAdmin(serverParams);
  const ordenes = data?.items ?? [];
  const total = data?.total ?? 0;

  const darDeBaja = useDarDeBajaOrdenCompra();
  const reactivar = useReactivarOrdenCompra();
  const qc = useQueryClient();

  // Carga desde XML: elegir archivo → previsualizar → confirmar.
  const fileRef = useRef<HTMLInputElement>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [preview, setPreview] = useState<OrdenCompraAdmin | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const onElegirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const dto = await previewOrdenXml(file);
      setArchivo(file);
      setPreview(dto);
    } catch (err) {
      toastErrorApi(err);
    }
  };

  const confirmarImportar = async () => {
    if (!archivo) return;
    setConfirmando(true);
    try {
      const dto = await importarOrdenXml(archivo);
      toastExito({ title: `Orden ${dto.clave} cargada`, message: "Importada desde XML." });
      void qc.invalidateQueries({ queryKey: qk.adminOrdenesCompra });
      setPreview(null);
      setArchivo(null);
    } catch (err) {
      toastErrorApi(err);
    } finally {
      setConfirmando(false);
    }
  };

  // Export a Excel · trae TODAS las páginas con la búsqueda actual.
  const obtenerTodas = async (): Promise<OrdenCompraResumen[]> => {
    const busqueda = filtroDebounced.trim();
    const todas: OrdenCompraResumen[] = [];
    for (let p = 1; ; p++) {
      const pagina = await listarOrdenesCompra({
        ...(busqueda ? { busqueda } : {}),
        page: p,
        pageSize: 100,
      });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  // Por (clave, año fiscal): si hay una versión activa y cuál es la última · define las
  // acciones por fila (una clave puede repetirse en años distintos). Se calcula sobre la
  // página visible: el backend ordena por clave + versión desc, así que las versiones de
  // una clave llegan contiguas (solo un grupo podría partirse en el borde de página).
  const infoPorGrupo = useMemo(() => {
    const m = new Map<string, { hayActiva: boolean; maxVersion: number }>();
    for (const o of ordenes) {
      const k = `${o.clave}|${o.anioFiscal ?? ""}`;
      const cur = m.get(k) ?? { hayActiva: false, maxVersion: 0 };
      m.set(k, { hayActiva: cur.hayActiva || o.activo, maxVersion: Math.max(cur.maxVersion, o.version) });
    }
    return m;
  }, [ordenes]);

  const abrirEditar = (id: string) => void navigate({ to: "/admin/ordenes-compra/$id", params: { id } });

  const confirmarDarDeBaja = async (o: { id: string; clave: string; anioFiscal: number | null }) => {
    const ok = await confirm({
      title: `Dar de baja la orden de compra ${o.clave}`,
      message: (
        <Text size="sm">
          La OC <strong>{o.clave}</strong>{o.anioFiscal ? ` (${o.anioFiscal})` : ""} dejará de
          estar disponible para consumo. <strong>No se elimina</strong>: se conservan sus
          conceptos y entradas para el histórico, y podrás reactivarla después.
        </Text>
      ),
      confirmLabel: "Dar de baja",
      confirmColor: "advertencia",
      icon: <IconArchive size={14} />,
    });
    if (ok) darDeBaja.mutate(o.id);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Órdenes de compra"
        description={`Mock administrable de SAP · ${total} orden(es).`}
        actions={
          <Button
            variant="light"
            leftSection={<IconFileImport size={14} />}
            onClick={() => fileRef.current?.click()}
          >
            Importar OC desde XML
          </Button>
        }
      />

      <input
        ref={fileRef}
        type="file"
        accept=".xml,text/xml,application/xml"
        onChange={onElegirArchivo}
        className="absolute h-px w-px opacity-0"
        style={{ pointerEvents: "none" }}
      />
      <OrdenPreviewModal
        orden={preview}
        confirmando={confirmando}
        onConfirm={confirmarImportar}
        onCancel={() => {
          setPreview(null);
          setArchivo(null);
        }}
      />

      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Input
            placeholder="Buscar por clave, proveedor o RFC…"
            value={filtro}
            onChange={(e) => setFiltro(e.currentTarget.value)}
            wrapperClassName="max-w-90 flex-1"
          />
          <BotonExportarExcel
            archivo="ordenes-compra"
            hoja="Órdenes de compra"
            obtenerFilas={obtenerTodas}
            columnas={[
              { header: "Clave", value: (o) => o.clave },
              { header: "Año fiscal", value: (o) => o.anioFiscal },
              { header: "Versión", value: (o) => o.version },
              { header: "Proveedor", value: (o) => o.proveedorNombre },
              { header: "RFC", value: (o) => o.proveedorRfc },
              { header: "Fecha", value: (o) => o.fecha },
              { header: "Total", value: (o) => o.total },
              { header: "Moneda", value: (o) => o.moneda },
              { header: "Conceptos", value: (o) => o.conceptos },
              { header: "Entradas", value: (o) => o.entradas },
              { header: "Estado", value: (o) => (o.activo ? "Activa" : "Baja") },
            ]}
          />
        </Group>

        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} variante="superior" />
        <Card padding="lg">
          <div className="overflow-x-auto">
            <Table verticalSpacing="sm">
              <Thead>
                <Tr>
                  <Th>Clave</Th>
                  <Th align="center">Año fiscal</Th>
                  <Th align="center">Versión</Th>
                  <Th>Proveedor</Th>
                  <Th>RFC</Th>
                  <Th align="right">Total</Th>
                  <Th align="center">Conceptos</Th>
                  <Th align="center">Entradas</Th>
                  <Th align="center">Estado</Th>
                  <Th />
                </Tr>
              </Thead>
              {!data ? (
                <TablaSkeleton dentroDeTabla columnas={[{}, { align: "center" }, { align: "center" }, {}, {}, { align: "right" }, { align: "center" }, { align: "center" }, { align: "center" }, { align: "right" }]} filas={PAGE_SIZE} />
              ) : (
                <Tbody>
                  {ordenes.map((o) => {
                    const info = infoPorGrupo.get(`${o.clave}|${o.anioFiscal ?? ""}`);
                    const esUltimaInactiva = !o.activo && !info?.hayActiva && o.version === info?.maxVersion;
                    return (
                      <Tr key={o.id} className={o.activo ? undefined : "opacity-55"}>
                        <Td className="font-mono">{o.clave}</Td>
                        <Td align="center"><Text size="xs" className="font-mono">{o.anioFiscal ?? "—"}</Text></Td>
                        <Td align="center"><Text size="xs" className="font-mono">v{o.version}</Text></Td>
                        <Td>{o.proveedorNombre}</Td>
                        <Td><Text size="xs" className="font-mono">{o.proveedorRfc}</Text></Td>
                        <Td align="right">{formatearMonto(o.total, o.moneda)}</Td>
                        <Td align="center"><Badge variant="light" color="slate">{o.conceptos}</Badge></Td>
                        <Td align="center"><Badge variant="light" color="slate">{o.entradas}</Badge></Td>
                        <Td align="center">
                          <Badge variant="light" color={o.activo ? "exito" : "slate"}>
                            {o.activo ? "Activa" : "Histórica"}
                          </Badge>
                        </Td>
                        <Td align="right">
                          <Group gap={4} className="flex-nowrap justify-end">
                            {o.activo && (
                              <>
                                <IconAction label="Editar (crea versión nueva)" size="sm" onClick={() => abrirEditar(o.id)}>
                                  <IconPencil size={14} />
                                </IconAction>
                                <IconAction label="Dar de baja" size="sm" iconColor="advertencia-600" onClick={() => confirmarDarDeBaja(o)}>
                                  <IconArchive size={14} />
                                </IconAction>
                              </>
                            )}
                            {esUltimaInactiva && (
                              <IconAction label="Reactivar" size="sm" iconColor="exito-600" loading={reactivar.isPending} onClick={() => reactivar.mutate(o.id)}>
                                <IconArrowBackUp size={14} />
                              </IconAction>
                            )}
                          </Group>
                        </Td>
                      </Tr>
                    );
                  })}
                  {ordenes.length === 0 && (
                    <Tr>
                      <Td colSpan={10}>
                        <Group justify="center" className="py-4">
                          <Text c="dimmed" size="sm">Sin órdenes de compra.</Text>
                        </Group>
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              )}
            </Table>
          </div>
        </Card>

        <PaginadorTabla cargando={!data} total={total} page={page} pageSize={PAGE_SIZE} onPageChange={setPage} sustantivo="órdenes" />
      </Stack>
    </div>
  );
}
